// TODO: Replace with eds-event-card EventInteraction enum
// This is a re-definition of the EventInteractions

import { TABS_CONFIG } from '@/constants/tabConfig';
import { Location } from '@/types';
import {
    DestinationEvent,
    FormattedEvent,
    isPromoCodeActive,
    areUrgencySignalsActive,
    getActiveUrgencySignals,
} from '@eventbrite/consumer-search.event-renderer';

/**
 * An event as it reaches ranking, in either of the two shapes the homepage feeds hold.
 *
 * The All / Today / This weekend tabs keep raw search results (snake_case `DestinationEvent`); the For-you tab keeps
 * already-transformed `FormattedEvent`s, because its recommendations endpoint camelCases them server side.
 */
export type RankableEvent = DestinationEvent | FormattedEvent;

// There is a working enum inside eds-event-card, but is not currently exported due to the lack of types for the rest of the components.
export enum EventInteractions {
    Impression = 'impression',
    Click = 'click',
    Hover = 'hover',
    HideAd = 'hide-ad',
    ReportAd = 'report-ad',
    Mounted = 'mounted',
    Follow = 'follow',
    Unfollow = 'unfollow',
}

export const PLACE_EVENT_INTERACTION = 'PlaceEventInteraction';

export type EventInteraction<ContextData = unknown> = {
    id: string;
    action: EventInteractions;
    event: EventTrackingData;
} & ContextData;

// This should be the whole event data, but since we are juggling between deprecated types and new ones, we will leave this as any for now
// TODO: Define a consistent type for events across eb-ui
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type EventTrackingData = any & {
    isPromoted: boolean;
    isFree: boolean;
    isOnline: boolean;
};

export type InteractionCustomEvent<ContextData = unknown> = CustomEvent<
    EventInteraction<ContextData>
>;

export type EventInteractionContext = {
    user: {
        id?: string;
        locale?: string;
        guestId?: string;
        sessionId?: string;
    };
    adId?: string;
    place: {
        name: string;
        position: number;
        page: number;
        context: any; // eslint-disable-line @typescript-eslint/no-explicit-any
        tabKey?: string;
        placementId?: string;
        absoluteRank?: number;
        subInterface?: {
            name?: string;
            position?: string;
        };
    };
    displayContext?: {
        hasOpenPromotion: boolean;
        hasBOGOLabel: boolean;
        urgencySignals?: string[];
    };
    venueId?: string;
    feedbackContext?: {
        email: string;
        reason: string;
        comment?: string;
    };
};

export type HTMLElementWithEvents = Pick<
    HTMLElement,
    'addEventListener' | 'removeEventListener' | 'dispatchEvent'
>;

//TODO: remove generic
export type GetTrackingInformation<T = unknown> = (eventInteraction: EventInteraction) => T;

export interface Subscribable {
    addEventListener(
        type: string,
        listener: (event: InteractionCustomEvent<EventInteractionContext>) => void,
    ): void;
}

/**
 * Reads promoted-ness off an event in either shape.
 *
 * Organic events omit the metadata entirely, so the result must be coerced: without `Boolean(...)`,
 * `false === undefined` drops every organic card from its own group and collapses its relative rank to 0.
 */
export function isEventPromoted(event: RankableEvent): boolean {
    return Boolean(
        (event as DestinationEvent)?.promoted_listing_metadata?.is_promoted_event ??
        (event as FormattedEvent)?.isPromoted,
    );
}

/**
 * Computes event ranks for tracking purposes
 *
 * Due to historical reasons, we compute both relative and absolute ranks:
 * - rank: Position within the same related event type (promoted vs organic)
 * - absoluteRank: Position within the entire list
 *
 * @param events - Array of all events
 * @param eventId - ID of the event to find
 * @param isPromoted - Whether the event is promoted
 * @returns Object with rank and absoluteRank
 */
export function computeEventRanks(
    events: RankableEvent[],
    eventId: string,
    isPromoted: boolean | undefined,
): { rank: number; absoluteRank: number } {
    if (!eventId || typeof isPromoted === 'undefined') {
        return {
            rank: 0,
            absoluteRank: 0,
        };
    }

    // Group by each event's promoted-ness as a boolean, matching the shared discover/search implementation (`isPromoted === e.isPromoted`).
    const eventsGroup = events.filter((e) => isPromoted === isEventPromoted(e));

    // absoluteRank has to match on promoted-ness as well as id.
    // An event can appear twice in one feed — once as a paid placement and once as an organic search result — and a
    // bare id lookup returns whichever copy comes first, so both cards report the same absoluteRank and the ad's
    // reported slot silently belongs to its organic twin.
    return {
        rank: eventsGroup.findIndex((e) => e.id === eventId) + 1,
        absoluteRank:
            events.findIndex((e) => e.id === eventId && isPromoted === isEventPromoted(e)) + 1,
    };
}

/**
 * Aggregates contextual information for an event interaction, including event data, user/session details, location,
 * search context, and ranking info. Designed for tracking analytics or logging purposes.
 *
 * Parameters:
 * - tabKey: The active tab identifier ('All', 'This Weekend', 'Today').
 * - events: All events in the bucket.
 * - event: The specific event object being interacted with.
 * - eventSearch: The event search object.
 * - userId: The ID of the current user.
 * - locale: User's locale string.
 * - sessionId: The current session's ID.
 * - guestId: Guest identifier for unauthenticated users.
 * - location: Location object for the user's context.
 *
 * Returns:
 *   EventInteractionWithContext object containing assembled context about the event, its position, user, and environment.
 */

export function aggregateContext({
    tabKey,
    events = [],
    event,
    userId,
    locale,
    location,
}: Omit<EventInteraction, 'action'> & {
    tabKey: string;
    events: RankableEvent[];
    userId?: string;
    locale?: string;
    location: Location;
}): EventInteractionContext {
    const tabConfig = TABS_CONFIG[tabKey];
    const hasOpenPromotion = isPromoCodeActive(event.openDiscount);
    const hasBOGOLabel = !!event.specialDiscounts?.hasBogoTickets;

    const hasUrgencySignal = areUrgencySignalsActive(event);
    const urgencySignals = hasUrgencySignal ? getActiveUrgencySignals(event.urgencySignals) : [];

    const { rank, absoluteRank } = computeEventRanks(events, event.id, event.isPromoted);

    return {
        user: {
            id: userId,
            locale,
        },
        adId: event?.promotedListingMetadata?.adId,
        place: {
            position: rank,
            absoluteRank,
            placementId: event?.promotedListingMetadata?.placementId,
            name: 'homepage',
            page: 1,
            context: {
                date_filter: tabConfig?.eventSearch?.dates ?? ['current_future'],
                tags: event.tags || [],
                place_id: location?.placeId,
                is_online: location?.isOnline,
                latitude: location?.latitude,
                longitude: location?.longitude,
            },
            tabKey: tabKey,
            subInterface: {
                name: 'events_in',
                position: '1',
            },
        },
        displayContext: {
            hasOpenPromotion,
            hasBOGOLabel,
            urgencySignals,
        },
        venueId: event?.venue?.id,
    };
}
